import { Formik } from 'formik'
import React, { Fragment, useRef, useState, type InputHTMLAttributes } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { VaccinationFormValues } from './VaccinationForm.interface'
import { VaccinationSchema } from './VaccinationForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Typography, Checkbox } from '@mui/material'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { VaccineType, vaccineFormTypes } from 'src/constants/vaccintion'
import { SecureMajor } from '@shopify/polaris-icons'
import { UploadVaccination } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Button } from 'src/components/shared/Button'
import { ClientSecureWrapper } from 'src/components/layout/client/ClientSecureWrapper'
import { StepLabels } from '../step-labels/StepLabels'

const initialValues: VaccinationFormValues = {
  date: '',
  brand: VaccineType.Comirnaty,
}

const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]

export const VaccinationForm = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep)

  const formikRef = useRef<any>()

  const [notVaccinated, setNotVaccinated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const { savedValues, onValuesChange, clearStorage } = useStepFormStorage<VaccinationFormValues>({
    storageKey: `step_vaccination_form_${currentStep?.id}`,
  })

  const handleFormSubmit = async (values: VaccinationFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      await createAsyncAction(
        dispatch,
        UploadVaccination.request({
          ...values,
          isVaccinated: !notVaccinated,
          stepId: currentStep?.id,
        }),
      )

      clearStorage()
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeNoVaccinated = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotVaccinated(event.target.checked)
  }

  const formInitialValues = savedValues ?? initialValues

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      innerRef={formikRef}
      validationSchema={notVaccinated ? undefined : VaccinationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ handleSubmit, values }) => {
        onValuesChange(values)

        return (
          <ClientSecureWrapper
            footer={
              <Fragment>
                <FormErrorMessage css={tw`mb-4`} error={error} />
                <Button
                  className={tw`w-full mt-6`}
                  loading={isLoading}
                  onClick={() => handleSubmit()}
                  data-testid="contractors-onboarding-vaccination-submit-btn"
                >
                  Submit and continue
                </Button>
              </Fragment>
            }
          >
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <SecureMajor width={20} />
            </div>
            <StepLabels />
            <Typography css={tw`my-2`} variant="h2">
              Vaccination information
            </Typography>
            <Typography css={tw`mb-4 text-shades-400`}>
              Enter details about your most recent vaccination.
            </Typography>
            <Typography css={tw`mb-1 uppercase text-shades-400`} variant="overline">
              Enter vaccine details
            </Typography>
            <div css={tw`bg-shades-0 p-4 rounded-xl`}>
              <FormikTextField
                name="date"
                css={tw`w-full mb-4`}
                label="Date of most recent vaccination"
                mask={DATE_MASK}
                placeholder="Enter date"
                inputProps={{ 'data-testid': 'contractors-onboarding-vaccination-date-input' }}
              />
              <div css={tw`w-full`}>
                <FormikSelect
                  name="brand"
                  label="Vaccine brand you received"
                  placeholder="Select vaccination brand"
                  options={vaccineFormTypes}
                  inputProps={{ 'data-testid': 'contractors-onboarding-vaccination-brand-input' }}
                />
              </div>
            </div>
            <div css={tw`bg-shades-0 p-3 mt-6 rounded-xl flex flex-row items-center`}>
              <Checkbox
                checked={notVaccinated}
                onChange={handleChangeNoVaccinated}
                inputProps={
                  {
                    'aria-label': 'controlled',
                    'data-testid': 'contractors-onboarding-vaccination-not-vaccinated-input',
                  } as InputHTMLAttributes<HTMLInputElement>
                }
              />
              <Typography css={tw`ml-2`}>I'm not vaccinated</Typography>
            </div>
          </ClientSecureWrapper>
        )
      }}
    </Formik>
  )
}
